import React, { useEffect, useState } from 'react';
import Reel from 'react-reel';
import { useCoolfoodContext } from './Store';
import { i18n } from '../App';
import { makeStyles } from '@material-ui/core/styles';
import { formatGain, determineUnitAfterConversion } from '../calculationTools';

const useStyles = makeStyles(theme => ({
  root: {},
  monitor: {
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: 14,
    padding: theme.spacing(2)
  },
  numPersons: {
    marginLeft: '16px',
    fontWeight: 600
  },
  title: {
    fontSize: 18,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    color: theme.pink
  },
  personsText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  personsTextValue: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(1)
  },
  text: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1)
  },
  gains: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: '4px',
    marginRight: '4px'
  },
  num: {
    fontWeight: 600,
    marginLeft: '4px',
    marginRight: '4px'
  },
  highlight: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.pink,
    whiteSpace: 'nowrap'
  },
  thanks: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    color: theme.green,
    whiteSpace: 'nowrap'
  }
}));

const reelTheme = {
  reel: {
    height: '1em',
    display: 'flex',
    alignItems: 'flex-end',
    overflowY: 'hidden',
    fontSize: '1em',
    fontWeight: '600',
    lineHeight: '1.10em'
  },
  group: {
    transitionDelay: '0ms',
    transitionTimingFunction: 'ease-in-out',
    transform: 'translate(0, 0)',
    height: '1em'
  },
  number: {
    height: '1em'
  }
};

const reelThemeActivePersons = {
  reel: {
    height: '1em',
    display: 'flex',
    alignItems: 'flex-end',
    overflowY: 'hidden',
    fontSize: '1.2em',
    fontWeight: '600',
    lineHeight: '1em',
    marginLeft: '16px'
  },
  group: {
    transitionDelay: '0ms',
    transitionTimingFunction: 'ease-in-out',
    transform: 'translate(0, 0)',
    height: '1em'
  },
  number: {
    color: '#e53e51',
    height: '1em'
  }
};

const reelThemeActive = {
  reel: {
    height: '1em',
    display: 'flex',
    alignItems: 'flex-end',
    overflowY: 'hidden',
    fontSize: '1.2em',
    fontWeight: '600',
    lineHeight: '1em',
    marginLeft: '4px',
    marginRight: '4px'
  },
  group: {
    transitionDelay: '0ms',
    transitionTimingFunction: 'ease-in-out',
    transform: 'translate(0, 0)',
    height: '1em'
  },
  number: {
    color: '#e53e51',
    height: '1em'
  }
};

export default function SmallScreen({ className }) {
  const classes = useStyles();

  const [globalState] = useCoolfoodContext();

  const [countUpCo2, setCountUpCo2] = useState(0);
  const [countUpMoney, setCountUpMoney] = useState(0);
  const [countUpPersons, setCountUpPersons] = useState(0);

  const [totalGains, setTotalGains] = useState(() => {
    const stats =
      globalState.happening.happening.challenge &&
      typeof globalState.happening.happening.challenge == 'object'
        ? globalState.happening.happening.challengeStats
        : globalState.happening.coolfoodStats;
    return {
      co2For1Year: stats.total_co2,
      eurFor1Year: stats.total_eur,
      gbpFor1Year: stats.total_gbp
    };
  });

  useEffect(() => {
    if (globalState.commitsValidated) {
      const totalGainsCopy = totalGains;
      globalState.commits.forEach(commit => {
        totalGainsCopy.co2For1Year += commit.gains.co2For1Year;
        totalGainsCopy.eurFor1Year += commit.gains.eurFor1Year;
        totalGainsCopy.gbpFor1Year += commit.gains.gbpFor1Year;
      });
      setTotalGains(totalGainsCopy);
    }
  }, [globalState.commits, globalState.commitsValidated, totalGains]);

  useEffect(() => {
    const totalCo2 = formatGain(
      totalGains.co2For1Year,
      globalState.language,
      { style: 'decimal' },
      true
    );

    let totalMoney;

    if (globalState.language === 'fr') {
      totalMoney = formatGain(
        Math.round(totalGains.eurFor1Year),
        globalState.language,
        {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        }
      );
    } else {
      totalMoney = formatGain(
        Math.round(totalGains.gbpFor1Year),
        globalState.language,
        {
          style: 'currency',
          currency: 'GBP',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        }
      );
    }

    setCountUpMoney(totalMoney);
    const dUnit = determineUnitAfterConversion(totalGains.co2For1Year);
    setCountUpCo2(`${totalCo2}${dUnit} eq CO₂`);
  }, [
    globalState.language,
    totalGains.co2For1Year,
    totalGains.eurFor1Year,
    totalGains.gbpFor1Year
  ]);

  useEffect(() => {
    let cnt =
      globalState.happening.happening.challenge &&
      typeof globalState.happening.happening.challenge == 'object'
        ? globalState.happening.happening.challengeStats.number_of_beneficiaries
        : globalState.happening.coolfoodStats.number_of_beneficiaries;
    if (globalState.commitsValidated) {
      //cnt = globalState.coolfooder.exists ? cnt : cnt+=1;
      cnt += 1;
    }
    // const personsLabel = i18n._('personnes');
    // setCountUpPersons(`${cnt} ${personsLabel}`);
    setCountUpPersons(`${cnt}`);
  }, [globalState.commitsValidated, globalState.happening]);

  return (
    <div className={className}>
      {!!globalState.happening.coolfoodStats && !!globalState.language && (
        <div id="monitor" className={classes.root}>
          <div id="monitorscreen" className={classes.monitor}>
            {!!globalState.happening.happening.challenge &&
              typeof globalState.happening.happening.challenge == 'object' && (
              <div className={classes.title}>
                {i18n._('Challenge en cours :')}
                {/* {' '}
                {globalState.happening.happening.challenge.name} */}
              </div>
            )}
            <div className={classes.personsText}>
              <div>{i18n._('personnes ont déjà relevé des défis !')}</div>
              <div className={classes.personsTextValue}>
                {globalState.commitsValidated ? (
                  <>
                    <Reel
                      theme={
                        globalState.commitsValidated
                          ? reelThemeActivePersons
                          : reelTheme
                      }
                      text={countUpPersons.toString()}
                    />
                    <div style={{minWidth: '4px'}}>{' '}</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {i18n._('personnes')}
                    </div>
                  </>
                ) : (
                  <div className={classes.numPersons}>
                    {countUpPersons}{' '}{i18n._('personnes')}
                  </div>
                )}
              </div>
            </div>
            <div className={classes.text}>
              {i18n._('D\'ici 1 an, cela représente au total un gain de :')}
            </div>
            <div className={classes.gains}>
              {globalState.commitsValidated ? (
                <Reel
                  theme={
                    globalState.commitsValidated ? reelThemeActive : reelTheme
                  }
                  text={countUpMoney}
                />
              ) : (
                <div className={classes.num}>{countUpMoney}</div>
              )}
              {i18n._('et')}
              {globalState.commitsValidated ? (
                <Reel
                  theme={
                    globalState.commitsValidated ? reelThemeActive : reelTheme
                  }
                  text={countUpCo2}
                />
              ) : (
                <div className={classes.num}>{countUpCo2}</div>
              )}
            </div>
            {!globalState.happening.happening.challenge &&
              (!globalState.commitsValidated ? (
                <div className={classes.highlight}>
                  {i18n._('CONTRIBUEZ VOUS AUSSI !')}
                </div>
              ) : (
                <div className={classes.thanks}>
                  {i18n._('MERCI POUR VOTRE CONTRIBUTION !')}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
